import { validateFieldLengths } from "../../../globals/utils/bondFieldLimits";
import {
  validateRequiredPaths,
  appendGuardianRequiredPaths,
  validateOccupationForHighBond,
  HIGH_BOND_AMOUNT_THRESHOLD,
  getUnderwritingThreshold,
} from "./validateStepHelpers";
import { parseCurrencyToNumber, safeNumber } from "./formHelpers";

export const BILLING_OTHER_REQUIRED_FIELDS = [
  "billing_other_name",
  "billing_other_address",
  "billing_other_city",
  "billing_other_state",
  "billing_other_zip",
];

export const STANDARD_PRINCIPAL_FIELD_NAMES = [
  "name",
  "address",
  "zip_code",
  "city",
  "state",
];

export const GUARDIANSHIP_PRINCIPAL_FIELD_NAMES = [
  "guardian_name",
  "guardian_street_address_1",
  "guardian_zip",
  "guardian_city",
  "guardian_state",
];

const ATTORNEY_REQUIRED_FIELDS = ["lawFirm", "lawFirmAddress", "attorneyOfRecord"];

const REQUESTOR_REQUIRED_FIELDS = [
  "firstNameReq",
  "lastNameReq",
  "email",
  "phoneNumber",
];

/** @param {number} bondAmount */
export function isHighBondAmount(bondAmount) {
  return safeNumber(bondAmount) >= HIGH_BOND_AMOUNT_THRESHOLD;
}

/** @param {object} state */
export function getBondAmountFromState(state) {
  return safeNumber(parseCurrencyToNumber(state?.basicInformation?.bondAmount));
}

/** @param {string[]} fields @param {number} step @param {object} state */
export function appendHighBondAuthField(fields, step, state) {
  if (step === 1 && isHighBondAmount(getBondAmountFromState(state))) {
    fields.push("requestForAuthorization");
  }
  return fields;
}

/** @param {string[]} fields @param {object} data */
export function appendBillingOtherRequiredFields(fields, data) {
  if (data?.billing_entity === "Other") {
    fields.push(...BILLING_OTHER_REQUIRED_FIELDS);
  }
  return fields;
}

/** @param {string[]} fields @param {object} data @param {"conditional" | "always"} mode */
export function appendAttorneyRequiredFields(fields, data, mode = "conditional") {
  if (mode === "always" || data?.attorneyInvolved === "Yes") {
    fields.push(...ATTORNEY_REQUIRED_FIELDS);
  }
  return fields;
}

/** Matches trustee/guardianship: values stored as "Unemployed" or "Employed - Accountant". */
export function splitEmploymentOccupationSeparators(raw) {
  if (!raw || typeof raw !== "string") {
    return { employmentStatus: "", occupationText: "" };
  }
  const separators = [" - ", " – ", " — "];
  for (const sep of separators) {
    const idx = raw.indexOf(sep);
    if (idx !== -1) {
      return {
        employmentStatus: raw.slice(0, idx).trim(),
        occupationText: raw.slice(idx + sep.length).trim(),
      };
    }
  }
  return { employmentStatus: raw.trim(), occupationText: "" };
}

/** Matches VABond.jsx: values stored as "Unemployed" or "Employed - Accountant" etc. */
export function splitEmploymentOccupationDash(raw) {
  const value = (raw || "").trim();
  if (!value) return { employmentStatus: "", occupationText: "" };
  const parts = value.split("-").map((p) => p.trim()).filter(Boolean);
  if (parts.length === 0) return { employmentStatus: "", occupationText: "" };
  const recognized = new Set(["Employed", "Unemployed", "Retired"]);
  const first = parts[0];
  if (!recognized.has(first)) {
    return { employmentStatus: "Employed", occupationText: value };
  }
  return { employmentStatus: first, occupationText: parts.slice(1).join(" - ") };
}

/** @param {import("./bondFormConfig").BondFormKindConfig} config */
function getSplitEmploymentFn(config) {
  return config.validation?.splitEmploymentOccupation === "dash"
    ? splitEmploymentOccupationDash
    : splitEmploymentOccupationSeparators;
}

function buildGuardianshipDynamicFields(g, bondAmt, bondSubCategory) {
  const inPgDropdown = g.is_professional_guardian === "Yes" && !g.showFullForm;
  const inAttorneyFlow = g.is_attorney === "Yes" && !g.showAttorneyForm;
  const inBranch3 = !inPgDropdown && !inAttorneyFlow;
  const underwritingThreshold = getUnderwritingThreshold(bondSubCategory);

  const fields = [...GUARDIANSHIP_PRINCIPAL_FIELD_NAMES];
  if (g.is_new_pg || g.is_esignatures_principal === "Yes") {
    fields.push("email");
  }

  const needEsign =
    (inPgDropdown && g.professional_guardian && !g.pg_gia_on_file) ||
    (inAttorneyFlow && g.attorney && !g.attorney_gia_on_file) ||
    inBranch3;
  if (needEsign) fields.push("is_esignatures_principal");

  const needCredit =
    bondAmt >= underwritingThreshold &&
    g.is_attorney !== "Yes" &&
    !(g.is_professional_guardian === "Yes" && g.pg_loa_on_file) &&
    ((inPgDropdown && g.professional_guardian) || inBranch3);
  if (needCredit) fields.push("credit_report_principal");

  return fields;
}

function collectGuardianshipRequiredFields(data, count, bondAmt, bondSubCategory) {
  const guardiansArr = data.guardians || [];
  const out = [];
  for (let i = 0; i < count; i++) {
    const g = guardiansArr[i] || {};
    buildGuardianshipDynamicFields(g, bondAmt, bondSubCategory).forEach((field) => {
      out.push(`guardians.${i}.${field}`);
    });
  }
  return out;
}

/** @param {import("./bondFormConfig").BondFormKindConfig} config */
function appendPrincipalRequiredFields(requiredFields, config, data, state) {
  const validation = config.validation;
  if (!validation) return requiredFields;

  if (validation.principalFieldMode === "guardianship") {
    const count = Number(state.basicInformation?.GuardianNumber || 1);
    const bondAmt = getBondAmountFromState(state);
    requiredFields.push(
      ...collectGuardianshipRequiredFields(
        data,
        count,
        bondAmt,
        state.basicInformation?.bondSubCategory
      )
    );
    return requiredFields;
  }

  const count = state.basicInformation?.[validation.principalCountField || "PrincipalNumber"] || 1;
  const dynamicFields = [...(validation.principalFieldNames || STANDARD_PRINCIPAL_FIELD_NAMES)];
  if (
    validation.requirePrincipalEmailOnEsign &&
    state.basicInformation?.is_esignatures === "Yes"
  ) {
    dynamicFields.push("email");
  }
  appendGuardianRequiredPaths(requiredFields, count, dynamicFields);
  return requiredFields;
}

/** @param {import("./bondFormConfig").BondFormKindConfig} config */
function appendOccupationErrorsIfHighBond(errs, config, data, state) {
  const validation = config.validation;
  if (!validation) return errs;

  if (!isHighBondAmount(getBondAmountFromState(state))) return errs;

  const count =
    state.basicInformation?.[validation.principalCountField || "PrincipalNumber"] || 1;
  Object.assign(
    errs,
    validateOccupationForHighBond(
      data,
      count,
      getSplitEmploymentFn(config),
      validation.occupationKey || "occupation"
    )
  );
  return errs;
}

/**
 * @param {import("./bondFormConfig").BondFormKindConfig} config
 * @param {object} data
 * @param {number} step
 * @param {object} state
 * @param {boolean} isGuestRequest
 */
export function validateBondFormStep(config, data, step, state, isGuestRequest) {
  let requiredFields = [
    ...config.getRequiredFieldsPerStep({ step, data, state, isGuestRequest }),
  ];

  if (step === config.validation?.principalStep) {
    appendPrincipalRequiredFields(requiredFields, config, data, state);
  }

  if (step === 7) {
    appendBillingOtherRequiredFields(requiredFields, data);
  }

  const errs = validateRequiredPaths(data, requiredFields);
  Object.assign(errs, validateFieldLengths(data, config.stepLimitsMap[step]));

  if (step === config.validation?.principalStep) {
    appendOccupationErrorsIfHighBond(errs, config, data, state);
  }

  return errs;
}

export {
  ATTORNEY_REQUIRED_FIELDS,
  REQUESTOR_REQUIRED_FIELDS,
};
